import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { iemService } from '@/services/iem';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildIeItemsNotOnPhrReportHtml,
  type IeItemsNotOnPhrItem,
  type IeItemsNotOnPhrTemplateOptions,
} from '@/reports/templates/ieItemsNotOnPhrTemplate';

interface RawIeSummaryRow {
  item: string;
  nsn: string;
  lin?: string | null;
  authorized_qty?: number | null;
  current_qty?: number | null;
  status: string;
  kit_name?: string | null;
  optional?: boolean | null;
  notes?: string | null;
  person_ie_kit_id?: string | null;
  kit_item_id?: string | null;
  ie_kit_id?: string | null;
  catalog_id?: string | null;
  kit_assignment_name?: string | null;
  available_qty?: number | null;
  person_display_name?: string | null;
}

interface PersonParam {
  id: string;
  displayName: string;
  entityIdentifier: string | null;
}

const parseCsvList = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

/**
 * Normalizes IE Kit filter input to a standardized array of non-empty strings.
 * Handles both array input (from multiselect) and comma-separated string (for backwards compatibility).
 * Returns empty array if input is null/undefined or contains only empty values.
 */
const normalizeIeFilter = (value: unknown): string[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());
  }

  if (typeof value === 'string') {
    return parseCsvList(value);
  }

  console.warn('Unexpected ieFilter type:', typeof value);
  return [];
};

/**
 * Formats an array of strings for display in report headers.
 * Returns null if array is empty, otherwise joins with ', '.
 */
const formatArrayForDisplay = (arr: string[]): string | null => {
  return arr.length > 0 ? arr.join(', ') : null;
};

const matchesAnyToken = (value: string, tokens: string[]): boolean => {
  const lowerValue = value.toLowerCase();
  return tokens.some((token) => lowerValue.includes(token.toLowerCase()));
};

export const runIeItemsNotOnPhrReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const personParam = params.person as PersonParam | null | undefined;
  const linSearchParam = (params.linSearch as string | undefined)?.trim() ?? '';
  // Normalize IE Kit filter input (handles both array from multiselect and CSV string)
  const ieTokens = normalizeIeFilter(params.ieFilter);

  const linTokens = parseCsvList(linSearchParam);

  const personId = personParam?.id ?? null;
  const individualDisplayName = personParam?.displayName ?? null;

  const endpoint = personId
    ? `/api/v1/ie_summary?person_id=eq.${personId}`
    : '/api/v1/ie_summary';

  const rows = await httpClient.get<RawIeSummaryRow[]>(endpoint);

  const filteredRows = rows.filter((row) => {
    const lin = row.lin ?? '';
    const kitName = row.kit_name ?? '';

    if (linTokens.length > 0 && (!lin || !matchesAnyToken(lin, linTokens))) {
      return false;
    }

    if (ieTokens.length > 0 && (!kitName || !matchesAnyToken(kitName, ieTokens))) {
      return false;
    }

    return true;
  });

  const billet = personId ? await iemService.getBilletForCustomer(personId) : null;
  const billetLabel =
    billet?.positionTitle?.trim() || billet?.dutyLocation?.trim() || billet?.deploymentStatus?.trim() || null;

  const items: IeItemsNotOnPhrItem[] = filteredRows
    .map((row) => {
      const authorized = row.authorized_qty ?? 0;
      const issued = row.current_qty ?? 0;
      const needed = authorized - issued;
      // When a specific individual is selected use the header display name; otherwise
      // use the per-row name resolved from the DB (person_display_name).
      const rowPersonName = personId
        ? (individualDisplayName ?? undefined)
        : (row.person_display_name ?? undefined);
      return {
        individualName: rowPersonName,
        nomenclature: row.item,
        nsn: row.nsn,
        lin: row.lin ?? null,
        qtyAuthorized: authorized,
        qtyIssued: issued,
        qtyNeeded: needed > 0 ? needed : 0,
      };
    })
    .filter((item) => (item.qtyNeeded ?? 0) > 0)
    .sort((a, b) => {
      // When showing all SMs, sort by service member name first
      if (!personId) {
        const smA = a.individualName ?? '';
        const smB = b.individualName ?? '';
        const smCompare = smA.localeCompare(smB);
        if (smCompare !== 0) return smCompare;
      }

      // Sort by LIN (nulls last)
      const linA = a.lin ?? '';
      const linB = b.lin ?? '';
      if (!linA && linB) return 1;
      if (linA && !linB) return -1;
      const linCompare = linA.localeCompare(linB);
      if (linCompare !== 0) return linCompare;

      // Then sort by Nomenclature
      return a.nomenclature.localeCompare(b.nomenclature);
    });

  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';

  const options: IeItemsNotOnPhrTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      individualName: individualDisplayName,
      billet: billetLabel,
      linSearch: linSearchParam || null,
      ieFilter: formatArrayForDisplay(ieTokens),
    },
    items,
    ieKitLabel,
  };

  const html = buildIeItemsNotOnPhrReportHtml(options);
  outputReport(html, 'ie-items-not-on-sm-phr.html', getOutputFormat(params));
};
